import { useReactFlow } from '@xyflow/react'
import React, { memo, useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { useUser } from '@/context/UserContext'
import { useProjectPhaseStore, ProjectPhase } from '@/stores/projectPhaseStore'
import '~/styles/OverlayBottomBar.css'

interface PhaseInfo {
  id: ProjectPhase
  name: string
  description: string
  subtitle: string
  progress: number
  items: {
    name: string
    completed: boolean
    inProgress?: boolean
  }[]
}

function OverlayBottomBar() {
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const currentPhase = useProjectPhaseStore(state => state.currentPhase)
  const setPhase = useProjectPhaseStore(state => state.setPhase)
  const setUserEmail = useProjectPhaseStore(state => state.setUserEmail)
  const user = useUser()
  const [showGlitch, setShowGlitch] = useState(false)
  const { getNodes } = useReactFlow()
  const totalNodes = getNodes().length
  
  useEffect(() => {
    // Set user email in the store when component mounts
    if (user && user.email) {
      setUserEmail(user.email)
    }
  }, [user, setUserEmail]);

  // Define project phases with their metadata
  const phases: Record<ProjectPhase, PhaseInfo> = {
    knowledge: {
      id: 'knowledge',
      name: 'KNOWLEDGE',
      description: 'Specify, Refine, Prepare',
      subtitle: 'Define project scope and requirements',
      progress: 75,
      items: [
        { name: 'Recommendation: Review requirements with stakeholders', completed: false },
        { name: 'Recommendation: Refine technical specs', completed: false },
        { name: 'Recommendation: Prepare knowledge extraction plan', completed: false }
      ]
    },
    resourcing: {
      id: 'resourcing',
      name: 'RESOURCING',
      description: 'Allocate, Find, Tender',
      subtitle: 'Secure necessary resources',
      progress: 30,
      items: [
        { name: 'Recommendation: Identify missing team roles', completed: false },
        { name: 'Recommendation: Start procurement process', completed: false },
        { name: 'Recommendation: Review budget allocations', completed: false }
      ]
    },
    production: {
      id: 'production',
      name: 'PRODUCTION',
      description: 'Manage, Oversee, Communicate',
      subtitle: 'Execute and deliver',
      progress: 10,
      items: [
        { name: 'Recommendation: Monitor development progress', completed: false },
        { name: 'Recommendation: Plan QA activities', completed: false },
        { name: 'Recommendation: Prepare deployment checklist', completed: false }
      ]
    },
    personal: {
      id: 'personal',
      name: 'PERSONAL',
      description: 'My Nodes',
      subtitle: 'View only nodes you created',
      progress: 100,
      items: [
        { name: 'Recommendation: Review your created nodes', completed: false },
        { name: 'Recommendation: Update node details', completed: false }
      ]
    }
  }

  const handlePhaseChange = (phase: ProjectPhase) => {
    if (phase !== currentPhase) {
      // Trigger glitch animation when changing phases
      //setShowGlitch(true) // commented to test how it feels without glitchy
      
      // Set the new phase
      setPhase(phase)
      
      // Broadcast a custom event to notify that phase has changed
      const phaseChangeEvent = new CustomEvent('phase-change', {
        detail: { phase, previousPhase: currentPhase }
      });
      document.dispatchEvent(phaseChangeEvent);
    }
    // Don't close dialog when changing tabs within it
  }

  // Add an effect to remove the glitch class after animation completes
  useEffect(() => {
    if (showGlitch) {
      const timer = setTimeout(() => {
        setShowGlitch(false)
      }, 1500) // Animation duration

      return () => clearTimeout(timer)
    }
  }, [showGlitch])

  const handleButtonClick = (phase: ProjectPhase) => {
    // If clicking on the status button, open dialog
    if (phase === 'knowledge' && currentPhase === 'knowledge') {
      setShowStatusDialog(true)
    } else if (phase === 'resourcing' && currentPhase === 'resourcing') {
      setShowStatusDialog(true)
    } else if (phase === 'production' && currentPhase === 'production') {
      setShowStatusDialog(true)
    } else if (phase === 'personal' && currentPhase === 'personal') {
      setShowStatusDialog(true)
    } else {
      // Otherwise change the phase
      handlePhaseChange(phase)
    }
  }

  const getButtonVariant = (phase: ProjectPhase) => {
    return currentPhase === phase ? "outline" : "ghost"
  }

  return (
    <div className={`overlay-bottom-bar ${showGlitch ? 'with-glitch' : ''}`}>
      <div className="phase-buttons-container">
        <Button 
          variant="outline"
          className="phase-button status-button"
          onClick={() => setShowStatusDialog(true)}
        >
          <div className="phase-button-content">
            <div className="phase-description">Project</div>
            <div className="phase-name">0. STATUS</div>
          </div>
        </Button>

        <Button 
          variant={getButtonVariant('knowledge')}
          className="phase-button knowledge-button"
          onClick={() => handleButtonClick('knowledge')}
        >
          <div className="phase-button-content">
            <div className="phase-description">Specify, Refine, Prepare</div>
            <div className="phase-name">1. KNOWLEDGE</div>
          </div>
        </Button>

        <Button 
          variant={getButtonVariant('resourcing')}
          className="phase-button resourcing-button"
          onClick={() => handleButtonClick('resourcing')}
        >
          <div className="phase-button-content">
            <div className="phase-description">Allocate, Find, Tender</div>
            <div className="phase-name">2. RESOURCING</div>
          </div>
        </Button>

        <Button 
          variant={getButtonVariant('production')}
          className="phase-button production-button"
          onClick={() => handleButtonClick('production')}
        >
          <div className="phase-button-content">
            <div className="phase-description">Manage, Oversee, Communicate</div>
            <div className="phase-name">3. PRODUCTION</div>
          </div>
        </Button>
        
        <Button 
          variant={getButtonVariant('personal')}
          className="phase-button personal-button"
          onClick={() => handleButtonClick('personal')}
        >
          <div className="phase-button-content">
            <div className="phase-description">Created by me</div>
            <div className="phase-name">x. PERSONAL</div>
          </div>
        </Button>
      </div>

      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Project Status Overview</DialogTitle>
            <DialogDescription>
              Current phase: {currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1)}
              {currentPhase === 'personal' && user?.email && (
                <span className="block text-sm mt-1">
                  Showing only nodes created by: {user.email}
                </span>
              )}
              <span className="block text-sm mt-1">
                Total nodes: {totalNodes}
              </span>
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue={currentPhase} onValueChange={(value) => handlePhaseChange(value as ProjectPhase)}>
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="knowledge">Knowledge</TabsTrigger>
              <TabsTrigger value="resourcing">Resourcing</TabsTrigger>
              <TabsTrigger value="production">Production</TabsTrigger>
              <TabsTrigger value="personal">Personal</TabsTrigger>
            </TabsList>

            {Object.values(phases).map((phase) => (
              <TabsContent key={phase.id} value={phase.id} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">{phase.name}</h3>
                    <p className="text-sm text-muted-foreground">{phase.subtitle}</p>
                  </div>
                  <Badge variant={phase.progress >= 100 ? "success" : "outline"}>
                    {phase.progress}% Complete
                  </Badge>
                </div>

                <Progress value={phase.progress} className="h-2" />

                <div className="space-y-2 mt-4">
                  {phase.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-blue-300"></div>
                      <span>
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default memo(OverlayBottomBar)